import { EnvironmentImpact } from "../../models/generated/graphql";
import { sortBySelectors } from "@lyfegen-platform/utils";
import { LyfeDialog } from "../dialogs/LyfeDialog";
import { Box, Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";

enum ErrorImpactKinds {
	EntityLocked = "entity-locked",
	DocumentedTransactionsImpacted = "documented-transactions-impacted",
	CaseDeleted = "case-deleted",
}

const impactPriorities = [ErrorImpactKinds.EntityLocked, ErrorImpactKinds.DocumentedTransactionsImpacted, ErrorImpactKinds.CaseDeleted];

const isErrorImpact = (kind: string) =>
	kind === ErrorImpactKinds.EntityLocked ||
	kind === ErrorImpactKinds.DocumentedTransactionsImpacted ||
	kind === ErrorImpactKinds.CaseDeleted;

export const getErrorImpact = (errorImpacts: EnvironmentImpact[]) => {
	return (
		errorImpacts
			.filter((entity: { __typename?: "EnvironmentImpact"; kind: string; transactionIds?: Array<string> | null }) =>
				isErrorImpact(entity.kind)
			)
			.sort(sortBySelectors((impact) => impactPriorities.indexOf((impact as any).kind as ErrorImpactKinds)))[0] || null
	);
};

export interface IErrorImpactDialogProps {
	errorImpact: EnvironmentImpact | null;
	isCaseDeletionAllowed?: boolean;
	onAllowCaseDeletion?: () => void;
	onDiscardChange?: () => void;
	onClose?: () => void;
}

export const ErrorImpactDialog = (props: IErrorImpactDialogProps) => {
	const { errorImpact, isCaseDeletionAllowed, onAllowCaseDeletion, onClose } = props;
	const { t } = useTranslation();

	if (!errorImpact) {
		return null;
	}

	const showEntityLockedDialog = errorImpact.kind === ErrorImpactKinds.EntityLocked;
	const showCaseDeletedDialog = errorImpact.kind === ErrorImpactKinds.CaseDeleted && !isCaseDeletionAllowed;
	const showDocumentedTransactionsDialog = errorImpact.kind === ErrorImpactKinds.DocumentedTransactionsImpacted;

	return (
		<>
			<LyfeDialog title={t("cannot-proceed")} content={[t("entity-locked")]} isOpen={showEntityLockedDialog}>
				<Box width="100%" display="flex" justifyContent="flex-end" alignItems="center" p={1}>
					<Button variant="contained" color="primary" onClick={onClose} autoFocus>
						{t("close")}
					</Button>
				</Box>
			</LyfeDialog>

			<LyfeDialog title={t("cannot-proceed")} content={[t("case-deletion-confirmation")]} isOpen={showCaseDeletedDialog}>
				<Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={1}>
					<Button variant="outlined" color="primary" onClick={onClose} autoFocus>
						{t("no-cancel")}
					</Button>
					<Button variant="contained" color="primary" onClick={onAllowCaseDeletion}>
						{t("yes-proceed")}
					</Button>
				</Box>
			</LyfeDialog>

			<LyfeDialog
				title={t("cannot-proceed")}
				content={[
					t("changes-will-affect-transactions"),
					...(errorImpact.transactionIds || []),
					t("transactions-already-included-in-document"),
				]}
				isOpen={showDocumentedTransactionsDialog}
			>
				<Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={1}>
					<Button variant="outlined" color="primary" onClick={onClose} autoFocus>
						{t("cancel")}
					</Button>{" "}
				</Box>
			</LyfeDialog>
		</>
	);
};
